import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../Layout/Layout';

function TermsAndConditions() {
    return (
        <Layout>
            <div className="policy-page-wrapper bg-blue-50">
                <div className="container mt-5 mb-5">
                    <div className="row">
                        <div className="col-12">
                            <h1 className="mb-4">Terms and Conditions</h1>
                            <p className="text-muted mb-4"><strong>Last Updated:</strong> June 21, 2026</p>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">1. Agreement to Terms</h2>
                                <p>
                                    These Terms and Conditions (&quot;Terms&quot;) govern your access to and use of the NeuraTrader
                                    website, applications, and related services (collectively, the &quot;Service&quot;) operated by
                                    NeuraTrader Inc. (&quot;NeuraTrader,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
                                </p>
                                <p>
                                    By creating an account, accessing the dashboard, connecting an exchange API, or otherwise using
                                    the Service, you agree to be bound by these Terms and our{' '}
                                    <Link to="/privacy-policy" className="text-primary">Privacy Policy</Link>. If you do not agree,
                                    you must not use the Service.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">2. Description of the Service</h2>
                                <p>
                                    NeuraTrader provides software tools for cryptocurrency trading automation, including but not limited
                                    to grid bots, scheduled bots, algorithmic strategies, order templates, portfolio monitoring, and
                                    related analytics for spot and futures markets.
                                </p>
                                <p>
                                    NeuraTrader is a technology platform. We do not custody your funds, execute trades as a broker-dealer,
                                    or provide personalized investment advice. Trading decisions and configurations remain your responsibility.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">3. Eligibility</h2>
                                <ul>
                                    <li>You must be at least 18 years old (or the age of majority in your jurisdiction).</li>
                                    <li>You must have the legal capacity to enter into a binding agreement.</li>
                                    <li>You may not use the Service if cryptocurrency trading or automated trading tools are prohibited in your jurisdiction.</li>
                                    <li>You are responsible for ensuring your use of the Service complies with applicable laws, exchange rules, and tax obligations.</li>
                                </ul>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">4. Account Registration and Security</h2>
                                <p>
                                    You agree to provide accurate registration information and to keep your credentials confidential.
                                    You are responsible for all activity that occurs under your account, including actions taken through
                                    connected exchange API keys.
                                </p>
                                <p>
                                    Notify us immediately at{' '}
                                    <a href="mailto:neuratrader@gmail.com" className="text-primary">neuratrader@gmail.com</a>{' '}
                                    if you suspect unauthorized access to your account.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">5. Exchange API Keys and Third-Party Integrations</h2>
                                <p>
                                    To use trading features, you may connect API keys from supported cryptocurrency exchanges. You
                                    authorize NeuraTrader to use those credentials solely to perform the actions you configure within
                                    the Service, such as placing, modifying, or canceling orders according to your bot and strategy settings.
                                </p>
                                <ul>
                                    <li>You are solely responsible for the permissions granted to API keys (e.g., trading enabled, withdrawal disabled).</li>
                                    <li>We recommend using API keys with the minimum permissions required and enabling IP restrictions where supported by your exchange.</li>
                                    <li>NeuraTrader is not responsible for outages, rate limits, policy changes, or errors originating from third-party exchanges or data providers.</li>
                                </ul>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">6. Trading Risks and No Performance Guarantees</h2>
                                <p>
                                    <strong>Cryptocurrency trading involves substantial risk of loss.</strong> Digital asset markets are
                                    volatile, and automated strategies may perform differently in live conditions than in backtests or
                                    historical simulations.
                                </p>
                                <ul>
                                    <li>Past performance does not guarantee future results.</li>
                                    <li>Bots may execute unintended trades due to misconfiguration, market gaps, liquidity issues, network delays, or exchange errors.</li>
                                    <li>Futures and leveraged products carry additional risk, including liquidation and amplified losses.</li>
                                    <li>You should only trade with capital you can afford to lose.</li>
                                </ul>
                                <p>
                                    NeuraTrader does not guarantee profits, uptime, execution speed, or error-free operation. You assume
                                    full responsibility for your trading activity and outcomes.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">7. Subscriptions, Fees, and Billing</h2>
                                <p>
                                    Certain features may require a paid subscription. Pricing, billing intervals, and included features
                                    are displayed at the time of purchase. By subscribing, you authorize us and our payment processors
                                    to charge applicable fees.
                                </p>
                                <ul>
                                    <li>Subscription fees are generally non-refundable except where required by law or explicitly stated otherwise.</li>
                                    <li>We may change pricing with reasonable notice; continued use after a price change constitutes acceptance.</li>
                                    <li>Failure to pay may result in suspension or downgrade of paid features.</li>
                                </ul>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">8. Acceptable Use</h2>
                                <p>You agree not to:</p>
                                <ul>
                                    <li>Use the Service for unlawful, fraudulent, or manipulative market activity.</li>
                                    <li>Attempt to gain unauthorized access to systems, accounts, or data.</li>
                                    <li>Reverse engineer, scrape, or interfere with the Service except as permitted by law.</li>
                                    <li>Upload malware or use the platform to harm other users or infrastructure.</li>
                                    <li>Misrepresent your identity or affiliation.</li>
                                    <li>Use the Service in a manner that violates exchange terms or applicable financial regulations.</li>
                                </ul>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">9. Intellectual Property</h2>
                                <p>
                                    The Service, including software, branding, documentation, and design, is owned by NeuraTrader or
                                    its licensors and protected by intellectual property laws. We grant you a limited, non-exclusive,
                                    non-transferable license to use the Service for your personal or internal business trading operations
                                    in accordance with these Terms.
                                </p>
                                <p>
                                    You retain ownership of your trading data and configurations. You grant us a license to process that
                                    data as needed to operate, secure, and improve the Service, consistent with our Privacy Policy.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">10. Service Availability and Modifications</h2>
                                <p>
                                    We strive to maintain reliable service but do not warrant uninterrupted or error-free availability.
                                    Maintenance, upgrades, third-party failures, or force majeure events may cause downtime.
                                </p>
                                <p>
                                    We may modify, suspend, or discontinue features at any time. Where practicable, we will provide
                                    notice of material changes that adversely affect paid subscribers.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">11. Disclaimer of Warranties</h2>
                                <p>
                                    THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND,
                                    WHETHER EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                                    PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL MEET YOUR REQUIREMENTS OR
                                    PRODUCE SPECIFIC TRADING RESULTS.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">12. Limitation of Liability</h2>
                                <p>
                                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEURATRADER AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND
                                    AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
                                    DAMAGES, INCLUDING LOST PROFITS, LOST DATA, TRADING LOSSES, OR BUSINESS INTERRUPTION, ARISING FROM
                                    YOUR USE OF THE SERVICE.
                                </p>
                                <p>
                                    OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE SHALL NOT
                                    EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO NEURATRADER IN THE TWELVE (12) MONTHS PRECEDING THE
                                    CLAIM OR (B) ONE HUNDRED U.S. DOLLARS (USD $100).
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">13. Indemnification</h2>
                                <p>
                                    You agree to indemnify and hold harmless NeuraTrader from claims, damages, losses, and expenses
                                    (including reasonable legal fees) arising from your use of the Service, your trading activity, your
                                    violation of these Terms, or your violation of any law or third-party rights.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">14. Suspension and Termination</h2>
                                <p>
                                    You may stop using the Service at any time and may request account closure through support channels.
                                    We may suspend or terminate access if you breach these Terms, pose a security risk, fail to pay
                                    applicable fees, or if required by law.
                                </p>
                                <p>
                                    Upon termination, your right to use the Service ends. Provisions that by their nature should survive
                                    (including risk disclaimers, limitation of liability, and indemnification) will remain in effect.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">15. Privacy and Cookies</h2>
                                <p>
                                    Our collection and use of personal information is described in our{' '}
                                    <Link to="/privacy-policy" className="text-primary">Privacy Policy</Link>. Our use of cookies and
                                    similar technologies is described in our{' '}
                                    <Link to="/cookie-policy" className="text-primary">Cookie Policy</Link>.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">16. Changes to These Terms</h2>
                                <p>
                                    We may update these Terms from time to time. Material changes will be reflected by updating the
                                    &quot;Last Updated&quot; date. Continued use of the Service after changes become effective constitutes
                                    acceptance of the revised Terms. If you do not agree to updated Terms, you must discontinue use of
                                    the Service.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">17. Governing Law and Disputes</h2>
                                <p>
                                    These Terms are governed by the laws of the jurisdiction in which NeuraTrader operates, without regard
                                    to conflict-of-law principles. Any dispute arising from these Terms or the Service shall be resolved
                                    through good-faith negotiation. If unresolved, disputes may be submitted to binding arbitration or
                                    competent courts as permitted by applicable law.
                                </p>
                            </section>

                            <section className="mb-5">
                                <h2 className="h4 mb-3">18. Contact Us</h2>
                                <p>If you have questions about these Terms, contact us:</p>
                                <ul>
                                    <li><strong>Email:</strong> <a href="mailto:neuratrader@gmail.com" className="text-primary">neuratrader@gmail.com</a></li>
                                    <li><strong>Contact form:</strong> <Link to="/contact" className="text-primary">Contact page</Link></li>
                                </ul>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default TermsAndConditions;